import { ChevronDownIcon, FlaskConicalIcon } from "lucide-react";
import { observer } from "mobx-react-lite";

import { usePlayground } from "@/components/Playground/playground-store";
import { Switch } from "@/components/switch";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

interface MockToolControlsProps {
  isSizeXS?: boolean;
  /**
   * Standalone renders a fully-rounded, labelled button (used where there is no
   * Send button to join, e.g. the tool-response panel). Default is the caret
   * segment joined to the right of Send.
   */
  standalone?: boolean;
}

/**
 * Mock tool responses control. While enabled, every tool call in a chat run is
 * answered by an AI-generated mock (optionally steered by a scenario) instead of
 * executing tool code. Darker blue when on. Rendered as a split-button caret joined
 * to Send by default, or standalone where no Send button is present.
 */
export const MockToolControls = observer(
  ({ isSizeXS = false, standalone = false }: MockToolControlsProps) => {
    const playground = usePlayground();
    const isEnabled = playground.mockToolResponses;

    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Mock tool responses"
            title={isEnabled ? "Mocking tool responses" : "Mock tool responses"}
            className={cn(
              "inline-flex items-center gap-1 border text-xs shadow-sm transition-all duration-200 ease-in-out hover:brightness-105",
              isSizeXS ? "h-7 px-1.5" : "h-8 px-2",
              standalone ? "rounded-md" : "rounded-l-none rounded-r-md",
              isEnabled
                ? "border-blue-300 bg-blue-100 text-blue-800 hover:bg-blue-200"
                : "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100",
            )}
          >
            <FlaskConicalIcon className={cn(isSizeXS ? "h-3 w-3" : "h-3.5 w-3.5")} />
            {standalone && (
              <span className="whitespace-nowrap font-medium leading-none">
                {isEnabled ? "Mock tools: on" : "Mock tools"}
              </span>
            )}
            <ChevronDownIcon className={cn(isSizeXS ? "h-2.5 w-2.5" : "h-3 w-3")} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" side="top" className="w-80 p-4">
          <div className="flex flex-col gap-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold">Mock tool responses</h3>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Answer every tool call with an AI-generated mock instead of running tool code.
                </p>
              </div>
              <Switch
                checked={isEnabled}
                onChange={checked => playground.setMockToolResponses(checked)}
              />
            </div>

            {isEnabled && (
              <>
                <Textarea
                  value={playground.mockScenario}
                  onChange={e => playground.setMockScenario(e.target.value)}
                  placeholder="Scenario (optional), e.g. every tool fails with a timeout…"
                  className="min-h-[80px] resize-none p-3 text-sm"
                />
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-600">
                    Stop when the agent calls (optional)
                  </label>
                  <Input
                    value={playground.mockStopTools}
                    onChange={e => playground.setMockStopTools(e.target.value)}
                    placeholder="Comma-separated tool names, e.g. submit, finish"
                    className="text-sm"
                  />
                </div>
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  },
);
